import {
  Search,
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
  ExpandLess,
  Settings,
  Email,
  Domain as DomainIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
  ListSubheader,
  Paper,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { hostname } from "../utils";
import {
  INBOX_TYPE_GMAIL,
  INBOX_TYPE_OFFICE365,
  INBOX_TYPE_DOMAIN,
} from "../constants";
import googleLogo from "../img/google-logo.png";
import office365Logo from "../img/office-365-logo.png";

export interface Inbox {
  id: string;
  name: string;
  domain: string;
  inboxType?: string;
  integrationId?: string;
  sharedWith?: string[];
}

/**
 * Load all inboxes for a company, handling pagination automatically.
 * Fetches up to 1000 inboxes per request and continues fetching if there are more.
 */
export async function loadAllInboxes(
  token: string,
  companyId: string,
  endpoint: string = "/inboxes/for-selection"
): Promise<Inbox[]> {
  const allInboxes: Inbox[] = [];
  let page = 1;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    const res = await axios.get(`${hostname}${endpoint}`, {
      params: { companyId, page, pageSize },
      headers: { Authorization: `Bearer ${token}` },
    });

    const items = res.data.items || [];
    allInboxes.push(...items);

    // Check if there are more pages
    const total = res.data.total || 0;
    hasMore = allInboxes.length < total;
    page++;

    // Safety limit to prevent infinite loops
    if (page > 100) break;
  }

  return allInboxes;
}

// Multi-select props
interface MultiSelectProps {
  multiple: true;
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  selectedId?: never;
  onChangeSingle?: never;
}

// Single-select props
interface SingleSelectProps {
  multiple?: false;
  selectedId: string;
  onChangeSingle: (selectedId: string, inbox: Inbox | null) => void;
  selectedIds?: never;
  onChange?: never;
}

interface BaseProps {
  inboxes: Inbox[];
  label?: string;
  helperText?: string | React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  onManageInboxes?: () => void;
}

type SearchableInboxSelectProps = BaseProps &
  (MultiSelectProps | SingleSelectProps);

interface GroupedInboxes {
  domain: string;
  inboxes: Inbox[];
}

// Get inbox type icon
const getInboxTypeIcon = (inboxType?: string) => {
  switch (inboxType) {
    case INBOX_TYPE_GMAIL:
      return (
        <Box
          component="img"
          src={googleLogo}
          alt="Gmail"
          sx={{ width: 16, height: 16 }}
        />
      );
    case INBOX_TYPE_OFFICE365:
      return (
        <Box
          component="img"
          src={office365Logo}
          alt="Office 365"
          sx={{ width: 16, height: 16 }}
        />
      );
    case INBOX_TYPE_DOMAIN:
      return <DomainIcon sx={{ fontSize: 16, color: "text.secondary" }} />;
    default:
      return <Email sx={{ fontSize: 16, color: "text.secondary" }} />;
  }
};

const getInboxTypeLabel = (inboxType?: string) => {
  switch (inboxType) {
    case INBOX_TYPE_GMAIL:
      return "Gmail";
    case INBOX_TYPE_OFFICE365:
      return "Office 365";
    case INBOX_TYPE_DOMAIN:
      return "Domain";
    default:
      return "IMAP";
  }
};

const SearchableInboxSelect: React.FC<SearchableInboxSelectProps> = (props) => {
  const {
    inboxes,
    label = "Inbox",
    helperText,
    disabled = false,
    error = false,
    fullWidth = true,
    loading = false,
    onManageInboxes,
  } = props;

  // Normalize to work with arrays internally
  const isMultiple = props.multiple === true;
  const selectedIds = isMultiple
    ? props.selectedIds
    : props.selectedId
    ? [props.selectedId]
    : [];
  const handleChange = (newIds: string[]) => {
    if (isMultiple) {
      props.onChange(newIds);
    } else {
      const selectedInbox =
        newIds.length > 0
          ? inboxes.find((i) => i.id === newIds[0]) || null
          : null;
      props.onChangeSingle(newIds.length > 0 ? newIds[0] : "", selectedInbox);
    }
  };

  const [inputValue, setInputValue] = useState("");

  // Extract domain from inbox - handle full email addresses
  const extractDomain = (inbox: Inbox): string => {
    const domain = inbox.domain || "No Domain";
    if (domain.includes("@")) {
      return domain.split("@")[1];
    }
    return domain;
  };

  // Group inboxes by email domain
  const groupedInboxes = useMemo(() => {
    const groups: Record<string, Inbox[]> = {};

    inboxes.forEach((inbox) => {
      const domain = extractDomain(inbox);
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(inbox);
    });

    // Sort domains alphabetically, then sort inboxes within each domain
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([domain, domainInboxes]) => ({
        domain,
        inboxes: domainInboxes.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [inboxes]);

  // Initialize collapsed domains with all domains collapsed by default
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(
    () => new Set(groupedInboxes.map((g) => g.domain))
  );

  // Update collapsed domains when new domains appear
  useEffect(() => {
    const currentDomains = new Set(groupedInboxes.map((g) => g.domain));
    setCollapsedDomains((prev) => {
      const newDomains = [...currentDomains].filter((d) => !prev.has(d));
      if (newDomains.length > 0) {
        return new Set([...prev, ...newDomains]);
      }
      return prev;
    });
  }, [groupedInboxes]);

  // Flatten inboxes with domain info for autocomplete
  const flattenedOptions = useMemo(() => {
    const options: Array<Inbox & { groupDomain: string }> = [];
    groupedInboxes.forEach(({ domain, inboxes: domainInboxes }) => {
      domainInboxes.forEach((inbox) => {
        options.push({ ...inbox, groupDomain: domain });
      });
    });
    return options;
  }, [groupedInboxes]);

  const selectedInboxes = inboxes.filter((i) => selectedIds.includes(i.id));

  const toggleDomainCollapse = (domain: string) => {
    setCollapsedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  const selectAllInDomain = (domain: string) => {
    // Only available in multi-select mode
    if (!isMultiple) return;

    const domainInboxIds = inboxes
      .filter((i) => extractDomain(i) === domain)
      .map((i) => i.id);

    const allSelected = domainInboxIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      // Deselect all in domain
      handleChange(selectedIds.filter((id) => !domainInboxIds.includes(id)));
    } else {
      // Select all in domain
      const newIds = [...new Set([...selectedIds, ...domainInboxIds])];
      handleChange(newIds);
    }
  };

  const handleToggle = (inbox: Inbox) => {
    const isSelected = selectedIds.includes(inbox.id);
    if (isMultiple) {
      if (isSelected) {
        handleChange(selectedIds.filter((id) => id !== inbox.id));
      } else {
        handleChange([...selectedIds, inbox.id]);
      }
    } else {
      // Single select - just set the value (or clear if clicking selected)
      handleChange(isSelected ? [] : [inbox.id]);
    }
  };

  // For single select, get the single selected inbox
  const singleSelectedInbox =
    !isMultiple && selectedIds.length > 0
      ? flattenedOptions.find((i) => i.id === selectedIds[0]) || null
      : null;

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <Autocomplete
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        disabled={disabled || loading}
        loading={loading}
        options={flattenedOptions}
        value={isMultiple ? selectedInboxes : singleSelectedInbox}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onChange={(_, newValue) => {
          if (isMultiple) {
            handleChange((newValue as Inbox[]).map((i) => i.id));
          } else {
            handleChange(newValue ? [(newValue as Inbox).id] : []);
          }
        }}
        getOptionLabel={(option) => `${option.name} (${option.domain})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        groupBy={(option) =>
          (option as Inbox & { groupDomain: string }).groupDomain
        }
        filterOptions={(options, { inputValue }) => {
          const searchLower = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.name.toLowerCase().includes(searchLower) ||
              option.domain.toLowerCase().includes(searchLower)
          );
        }}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} sx={{ maxHeight: 400 }}>
            {children}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              selectedIds.length === 0
                ? loading
                  ? ""
                  : "Search inboxes..."
                : ""
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  {selectedIds.length === 0 && !loading && (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  )}
                  {loading && (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </InputAdornment>
                  )}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...(isMultiple && {
          renderTags: (value: Inbox[]) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {value.slice(0, 3).map((inbox) => (
                <Chip
                  key={inbox.id}
                  label={inbox.name}
                  size="small"
                  onDelete={() =>
                    handleChange(selectedIds.filter((id) => id !== inbox.id))
                  }
                />
              ))}
              {value.length > 3 && (
                <Chip
                  label={`+${value.length - 3} more`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          ),
        })}
        renderGroup={(params) => {
          const domain = params.group;
          const domainInboxIds = inboxes
            .filter((i) => extractDomain(i) === domain)
            .map((i) => i.id);
          const selectedInDomain = domainInboxIds.filter((id) =>
            selectedIds.includes(id)
          ).length;
          const allSelected =
            domainInboxIds.length > 0 &&
            selectedInDomain === domainInboxIds.length;
          const someSelected = selectedInDomain > 0 && !allSelected;
          const isCollapsed = collapsedDomains.has(domain);

          return (
            <Box key={params.key}>
              <ListSubheader
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[200],
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300],
                  },
                  py: 0.5,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDomainCollapse(domain);
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isMultiple && (
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllInDomain(domain);
                      }}
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                    />
                  )}
                  <Typography variant="subtitle2" fontWeight="bold">
                    @{domain}
                  </Typography>
                  {isMultiple && (
                    <Chip
                      label={`${selectedInDomain}/${domainInboxIds.length}`}
                      size="small"
                      color={allSelected ? "primary" : "default"}
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                  {!isMultiple && (
                    <Chip
                      label={domainInboxIds.length}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
                {isCollapsed ? (
                  <ExpandMore fontSize="small" />
                ) : (
                  <ExpandLess fontSize="small" />
                )}
              </ListSubheader>
              <Collapse in={!isCollapsed}>{params.children}</Collapse>
            </Box>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;

          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                px: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                handleToggle(option);
              }}
            >
              {isMultiple ? (
                <Checkbox
                  checked={selected}
                  size="small"
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                />
              ) : (
                <Radio checked={selected} size="small" />
              )}
              {getInboxTypeIcon(option.inboxType)}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" noWrap>
                    {option.name}
                  </Typography>
                  <Chip
                    label={getInboxTypeLabel(option.inboxType)}
                    size="small"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.domain}
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
      {(helperText || onManageInboxes) && (
        <FormHelperText component="div">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {helperText && <span>{helperText}</span>}
            {onManageInboxes && (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={(e) => {
                  e.preventDefault();
                  onManageInboxes();
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Settings sx={{ fontSize: 14 }} />
                Manage inboxes
              </Link>
            )}
          </Box>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SearchableInboxSelect;
